import React from "react";
import TaskForm from "../components/TaskForm";
import axios from "../axios/axios";
import {Alert, AlertIcon, FormControl, FormLabel, Input} from "@chakra-ui/react";
import {useState, useEffect, useCallback} from "react";
import parseTimeInput from "../lib/helpers/parseTimeInput";
import {format, isWithinInterval, subWeeks} from "date-fns";

import {useTasks} from "../providers/TasksProvider";

export default function TaskFormContainer({user}) {
    const [clients, setClients] = useState([]);
    const [clientProjects, setClientProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [taskData, setTaskData] = useState({
        taskId: "",
        taskName: "",
        taskCode: "",
        timeSpent: "",
        clientName: "",
        projectName: "",
        taskDescription: "",
    });

    const [errorMessage, setErrorMessage] = useState("");

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isDateInCurrentMonth, setIsDateInCurrentMonth] = useState(true);

    // Calcul des bornes de date
    const currentDate = new Date();
    const beforeDate = subWeeks(currentDate, 4);

    const {fetchTasks: fetchUserTasks} = useTasks();

    const fetchClients = useCallback(() => {
        axios
            .get("/clients?limit=1000")
            .then(response => setClients(response.data.clients))
            .catch(error => console.error("Error fetching clients:", error));
    }, []);

    const fetchTasks = useCallback(() => {
        axios
            .get(`/tasks?user_job_id=${user.user_job_id}`)
            .then(response => setTasks(response.data))
            .catch(error => console.error("Error fetching tasks:", error));
    }, [user.user_job_id]);

    useEffect(() => {
        fetchClients();
        fetchTasks();
    }, [fetchClients, fetchTasks]);

    const fetchClientsProjects = clientsId => {
        axios
            .get(`/project?client_id=${clientsId}`)
            .then(response => {
                setClientProjects(response.data);

                const selectedClient = clients.find(client => client.id === parseInt(clientsId, 10));

                if (selectedClient) {
                    setTaskData(prevTaskData => ({
                        ...prevTaskData,
                        clientName: selectedClient.client_name,
                    }));
                }
            })
            .catch(error => console.error("Error fetching client's projects:", error));
    };

    const handleClientChange = event => {
        const selectedClientId = event.target.value;

        if (selectedClientId) {
            fetchClientsProjects(selectedClientId);
        } else {
            setClientProjects([]);
            setTaskData(prevTaskData => ({
                ...prevTaskData,
                clientName: "",
                projectName: "",
            }));
        }
    };

    const handleProjectChange = event => {
        const selectedProjectId = event.target.value;
        if (selectedProjectId) {
            const selectedProject = clientProjects.find(project => project.id === parseInt(selectedProjectId, 10));

            if (selectedProject) {
                setTaskData(prevTaskData => ({
                    ...prevTaskData,
                    projectName: selectedProject.id,
                }));
            }
        } else {
            setTaskData(prevTaskData => ({
                ...prevTaskData,
                projectName: "",
            }));
        }
    };

    const handleTaskChange = event => {
        const {name, value} = event.target;

        setTaskData(prevTaskData => {
            // Si le champ modifié est `taskId`, mettez à jour `taskName` et `taskCode`
            if (name === "taskId") {
                const selectedTask = tasks.find(task => task.id === parseInt(value, 10));
                return {
                    ...prevTaskData,
                    taskId: value,
                    taskName: selectedTask ? selectedTask.task_name : "",
                    taskCode: selectedTask ? selectedTask.task_code : "",
                    taskDescription: "",
                };
            }

            // Si c'est la description de tâche
            if (name === "taskDescription") {
                return {
                    ...prevTaskData,
                    taskDescription: value,
                };
            }

            // Sinon, mettez à jour uniquement le champ modifié
            return {
                ...prevTaskData,
                [name]: value,
            };
        });
    };

    const handleAddTask = () => {
        const timeInHours = parseTimeInput(taskData.timeSpent);
        const timeSpent = Math.round(timeInHours * 60);

        if (!taskData.taskId || !taskData.timeSpent || timeSpent <= 0 || !taskData.clientName || !taskData.projectName) {
            setErrorMessage("Vous devez remplir tous les champs !");
            return;
        }

        // Vérifier si une description est requise pour le code 9999
        if (taskData.taskCode === "9999" && !taskData.taskDescription) {
            setErrorMessage("Une description est requise pour cette tâche !");
            return;
        }

        setErrorMessage("");

        axios
            .post("/user_task", {
                user_id: user.id,
                user_email: user.email,
                task_id: taskData.taskId,
                task_name: taskData.taskName,
                task_code: taskData.taskCode,
                task_description: taskData.taskCode === "9999" ? taskData.taskDescription : null,
                time_spent: timeSpent,
                client: taskData.clientName,
                projet: taskData.projectName,
                date: format(selectedDate, "yyyy-MM-dd"),
            })
            .then(response => {
                fetchUserTasks();
                // Réinitialiser le formulaire après l'ajout
                // setTaskData({
                //     taskId: "",
                //     taskName: "",
                //     taskCode: "",
                //     timeSpent: "",
                //     clientName: "",
                //     projectName: "",
                //     taskDescription: "",
                // });
            })
            .catch(error => console.error("Error posting time spent:", error));
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);

        // Vérification de la plage de dates : entre un mois avant et la date actuelle
        const isInValidRange = isWithinInterval(date, {
            start: beforeDate,
            end: currentDate,
        });

        setIsDateInCurrentMonth(isInValidRange);
    };

    return (
        <>
            <FormControl mb={4}>
                <FormLabel>Sélectionner une date</FormLabel>
                <Input
                    type="date"
                    value={format(selectedDate, "yyyy-MM-dd")}
                    onChange={e => handleDateChange(new Date(e.target.value))}
                    min={format(beforeDate, "yyyy-MM-dd")}
                    max={format(currentDate, "yyyy-MM-dd")}
                />
            </FormControl>
            {!isDateInCurrentMonth && (
                <Alert status="warning" mb={4}>
                    <AlertIcon/>
                    Cette date n'est pas dans le mois cours
                </Alert>
            )}
            {errorMessage && (
                <Alert status="error">
                    <AlertIcon/>
                    {errorMessage}
                </Alert>
            )}
            <TaskForm
                clients={clients}
                clientProjects={clientProjects}
                tasks={tasks}
                taskData={taskData}
                handleClientChange={handleClientChange}
                handleProjectChange={handleProjectChange}
                handleTaskChange={handleTaskChange}
                handleAddTask={handleAddTask}
            />
        </>
    );
}
